/* eslint-disable camelcase */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import {
  Row, Col, Table, DatePicker, Select,
} from 'antd';
import { getProcessorTransactionStats } from '../../redux/actions/Processors';
import TransactionStatsListColumn from './TransactionStatsListColumn';
import TransactionStatsSummary from './TransactionStatsSummary';
import { dateTimeRangeCondition, dateTimeRangeQueries } from '../../util/lib';

const { Option } = Select;
const { RangePicker } = DatePicker;

const TransactionStats = ({ processorId }) => {
  const dispatch = useDispatch();
  const [currency, setCurrency] = useState(null);
  const [dateRange, setDateRange] = useState([]);
  const [status, setStatus] = useState('ALL');
  const { timezone } = useSelector(({ settings }) => settings);
  const {
    data,
    loading,
    pageSize,
    currentPage,
    totalRecords,
  } = useSelector(({ processors }) => processors.transStats);

  const handleChange = (value, key) => {
    let _currency = currency;
    let _dateRange = dateRange;
    let _status = status;
    if (key === 'date_range') {
      _dateRange = value;
      setDateRange(value);
    } else if (key === 'currency') {
      _currency = value;
      setCurrency(value);
    } else if (key === 'status') {
      _status = value;
      setStatus(value);
    }

    if (_dateRange && _dateRange?.length && _currency) {
      let params = `limit=${pageSize}&${dateTimeRangeQueries(_dateRange[0], _dateRange[1], timezone)}`;
      if (_status && _status !== 'ALL') params += `&status=${_status}`;
      dispatch(getProcessorTransactionStats(processorId, _currency, params));
    }
  };

  const handleOnChange = (pagination, _filters, sort) => {
    if (pagination && sort) {
      const paginate = `page=${pagination.current}&limit=${pageSize}`;
      let sortParams = 'sort={"created_at": -1 }';
      if (Object.keys(sort).length && sort.field) {
        sortParams = `sort={"${sort.field}": ${sort.order === 'ascend' ? '1' : '-1'} }`;
      }
      let paginateAndSort = `${paginate}&${sortParams}`;
      if (dateRange && dateRange.length > 0) {
        const rangeStr = `${dateTimeRangeCondition(dateRange[0], dateRange[1], timezone)}`;
        paginateAndSort += `&filter={${rangeStr}}&${dateTimeRangeQueries(dateRange[0], dateRange[1], timezone)}`;
      }
      if (status && status !== 'ALL') {
        paginateAndSort += `&status=${status}`;
      }

      dispatch(getProcessorTransactionStats(processorId, currency, paginateAndSort));
    }
  };

  const props = {
    loading,
    scroll: 'y',
    rowKey: 'id',
    sticky: true,
    pagination: {
      current: currentPage,
      pageSize,
      total: totalRecords,
      showSizeChanger: false,
    },
    onChange: handleOnChange,
    columns: TransactionStatsListColumn(timezone),
    dataSource: data?.records || [],
  };

  const currencies = ['USD', 'EUR', 'UGX', 'KES', 'NGN', 'GHS'].map((cur) => (
    <Option key={cur} value={cur}>{cur}</Option>
  ));

  const statuses = ['ALL', 'COMPLETED', 'FAILED', 'CONFIRMING', 'CANCELLED', 'PENDING'].map((sts) => (
    <Option key={sts} value={sts}>{sts}</Option>
  ));

  return (
    <>
      <Row>
        <Col xl={16} lg={12} md={12} sm={12} xs={24}>
          {data && data.chart
            && (
              <Row>
                <Col xl={24} lg={24} md={24} sm={24} xs={24}>
                  <h2 className="title gx-mb-3">
                    Showing
                    {' '}
                    {data.chart}
                  </h2>
                </Col>
              </Row>
            )}
          <Row>
            <Col xl={6} lg={6} md={6} sm={6} xs={12}>
              <Select
                allowClear
                showArrow
                style={{ width: '100%' }}
                placeholder="Status"
                onChange={(e) => handleChange(e, 'status')}
                showSearch
                defaultValue="ALL"
                optionFilterProp="children"
                filterOption={(input, option) => option.children.toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0}
              >
                {statuses}
              </Select>
            </Col>
            <Col xl={6} lg={6} md={6} sm={6} xs={12}>
              <Select
                allowClear
                showArrow
                style={{ width: '100%' }}
                placeholder="Currency"
                onChange={(e) => handleChange(e, 'currency')}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => option.children.toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0}
              >
                {currencies}
              </Select>
            </Col>
            <Col xl={12} lg={12} md={12} sm={12} xs={24}>
              <RangePicker onChange={(_, dateString) => handleChange(dateString, 'date_range')} style={{ width: '100%' }} />
            </Col>
          </Row>
          <div className="gx-d-flex justify-content-center">
            <Table className="gx-table-responsive" {...props} />
          </div>
        </Col>

        <Col xl={8} lg={12} md={12} sm={12} xs={24}>
          <TransactionStatsSummary />
        </Col>
      </Row>
    </>
  );
};

TransactionStats.propTypes = {
  processorId: PropTypes.string.isRequired,
};

export default TransactionStats;
